import { Text, useToast } from '@chakra-ui/react';
import { useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { useAddUsersToCohort } from '../hooks/useAddUsersToCohort';
import { useGetCohorts } from '../hooks/useGetCohorts';

type Props = {
  userIds: string[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export default function SelectCohortModal({
  userIds,
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  const { selectedOrganization } = useSelectedOrganization();
  // R94-1 — why a refused read is a real state here, not a theoretical one:
  // `GET /cohorts?organizationId=` carries `AdminAuthGuard` +
  // `hasOrganizationAccess`, so a stale token or an org the operator is not an
  // admin of is refused, on top of the ordinary 5xx and network paths.
  // Post-R89-1 that leaves `data === undefined`.
  //
  // R117-1 — the read is now classified, not truthiness-tested (D59).
  // `useGetCohorts` is gated `enabled: Boolean(organizationId)`, and React
  // Query v4 parks a DISABLED query at `isLoading: true` with
  // `data === undefined` forever — so `cohorts ?? []` collapsed THREE states
  // into the one render below: a select holding only its placeholder behind a
  // submit that can never enable. The operator who just asked "which cohort?"
  // could not tell "you have no organization selected" from "this read was
  // refused" from "this organization has no cohorts".
  //
  // Note `classifyRead` takes the query's OWN flags rather than a restatement
  // of its `enabled:` expression, so this file cannot re-derive that gate
  // wrongly (D62). One consequence is deliberate and is a BEHAVIOUR CHANGE:
  // `unreadable` requires `!data`, so a BACKGROUND refetch that fails over
  // cohorts already held keeps the chooser rather than replacing it with the
  // error (R96-3) — the same trade runs 114-116 made.
  const {
    data: cohorts,
    isError: isCohortsError,
    isInitialLoading: isCohortsInitialLoading,
  } = useGetCohorts(selectedOrganization?.id);
  const cohortsReadState = classifyRead(
    isCohortsError,
    isCohortsInitialLoading,
    cohorts,
  );
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const toast = useToast();

  const { mutateAsync: addUsers, isLoading } =
    useAddUsersToCohort(selectedCohortId);

  const handleSubmit = async () => {
    if (!selectedCohortId) {
      toast({
        title: translate('cohort.no_cohort_selected'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const result = await addUsers({ userIds });

      toast({
        title: translate('cohort.users_added_successfully'),
        description: `${result.added} ${translate('cohort.added')}, ${result.skipped} ${translate('cohort.skipped')}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onSuccess?.();
      onModalClose();
    } catch (error) {
      toast({
        title: translate('cohort.add_users_failed'),
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const onModalClose = () => {
    setSelectedCohortId('');
    onClose();
  };

  const cohortOptions = (cohorts ?? []).map((cohort) => ({
    value: cohort.id,
    label: `${cohort.name} (${cohort.memberCount || 0} ${translate('cohort.members')})`,
  }));

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onModalClose}
      title={`${translate('cohort.select_cohort_for_users')} (${userIds.length})`}
      onSubmit={handleSubmit}
      submitLabel={translate('cohort.add_to_cohort')}
      isSubmitting={isLoading}
      isSubmitDisabled={!selectedCohortId}
    >
      {cohortsReadState === 'unreadable' ? (
        <InlineErrorState message={translate('cohort.error_loading')} />
      ) : cohortsReadState === 'loading' ? (
        // Genuinely in flight. Previously this rendered the chooser with zero
        // options, which is the same picture an organization with no cohorts
        // produces — so a slow read read as an empty one.
        <Text color="gray.500">{translate('cohort.loading')}</Text>
      ) : cohortsReadState === 'never-issued' ? (
        // No organization selected, so the read was never sent. A text line
        // rather than a `ScopePrompt` panel: this is one control inside a
        // form, not a page body (the call run 114 settled on for
        // `AddUserToGameModal` and run 116 for the Duo attach dialog).
        //
        // NOT REACHABLE from either caller today, and that is measured rather
        // than assumed — both caller guards AND this hook's `enabled:` premise
        // are pinned in `_shared/cohorts-read-error-gate.test.ts`
        // ("the modal never-issued arm is unreachable from BOTH callers").
        // `UserPage` disables the button that opens this dialog while no
        // organization is selected (R117-2), and `UserList` only mounts it
        // once at least one user is SELECTED, which requires a roster the
        // same missing scope prevents it from reading. The arm is written
        // anyway for D62's reason: a caller's guard is not this file's
        // property, and the next caller inherits no guard at all.
        <Text color="gray.500">
          {translate(scopePromptI18nKey('organization') as any)}
        </Text>
      ) : cohortOptions.length === 0 ? (
        // ANSWERED, and the answer is that there are none. A real empty state
        // and the only one of the four this component may state definitively
        // — kept separate from the three above precisely so it can be.
        <Text color="gray.500">{translate('cohort.no_cohorts')}</Text>
      ) : (
        <FormField
          type="select"
          label={translate('cohort.select_cohort')}
          value={selectedCohortId}
          onChange={setSelectedCohortId}
          placeholder={translate('cohort.choose_cohort')}
          options={cohortOptions}
        />
      )}
    </FormModal>
  );
}
